import { useQuery } from '@tanstack/react-query';
import axiosRetry from 'axios-retry';
import { createContext, useContext, useEffect, useMemo } from 'react';
import { featureFlagControllerGetFeatureFlags } from '@/api/gen/endpoints';
import { FeatureFlagName } from '@/api/gen/featureFlagName';
import { FeatureFlagStatus } from '@/api/gen/featureFlagStatus';
import axiosInstance from '@/lib/axiosInstance';
import { logger } from '@/utils/logger';

export interface FeatureFlagContextType {
  isFeatureEnabled: (featureFlag: FeatureFlagName) => boolean;
  isLoading: boolean;
}

export const FeatureFlagContext = createContext<FeatureFlagContextType>({
  isFeatureEnabled: () => false,
  isLoading: true,
});

const FEATURE_FLAG_QUERY_KEY = ['feature-flags', 'get-by-ids'] as const;
const featureFlagNameArray: FeatureFlagName[] = Object.values(FeatureFlagName);

export const FeatureFlagContextProvider = ({ children }: { children: React.ReactNode }) => {
  // Use the @Public() get-by-ids endpoint — the context mounts for every user
  // (including logged-out routes like Signup), so the Admin-gated GET /feature-flags
  // cannot be used here. Orval generates this POST as a mutation, so wrap the raw
  // fetcher in useQuery to preserve the original read-on-mount behaviour.
  const { data: featureFlags, isLoading } = useQuery({
    queryKey: FEATURE_FLAG_QUERY_KEY,
    queryFn: ({ signal }) => featureFlagControllerGetFeatureFlags(featureFlagNameArray, undefined, signal),
    staleTime: 60 * 60 * 1000, // 1 hour — flags rarely change; avoid refetch on remount
    gcTime: 24 * 60 * 60 * 1000, // 1 day — keep cached flag state across brief unmounts
    refetchOnWindowFocus: false,
  });

  const featureFlagMap = useMemo(() => {
    return new Map<string, boolean>(
      (featureFlags ?? []).map((flag) => [flag.name, flag.status === FeatureFlagStatus.ACTIVE && flag.value])
    );
  }, [featureFlags]);

  const isFeatureEnabled = (featureFlag: FeatureFlagName) => {
    return featureFlagMap.get(featureFlag) ?? false;
  };

  useEffect(() => {
    const MAX_RETRIES = 5;
    axiosRetry(axiosInstance, {
      retries: MAX_RETRIES,
      retryDelay: (retryCount, error) => {
        const code = error.code;
        const url = error.config?.url;
        logger.warn('Axios retrying', { code, url });

        return retryCount * 1000;
      },
    });
  }, []);

  return (
    <FeatureFlagContext.Provider
      value={{
        isFeatureEnabled,
        isLoading,
      }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const useFeatureFlagContext = () => useContext(FeatureFlagContext);
